import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { clsx } from '../ds/clsx';
import { Banner, Button, Eyebrow, Icon, KeyboardHint, Overlay, Skeleton, TextLink } from '../ds/components';
import { useEscapeKey, useFocusTrap, useIsMobile } from '../ds/hooks';
import { lockBodyScroll } from '../ds/components/Sheet';
import { errorMessage } from '../lib/api';
import { isMacPlatform } from '../lib/platform';
import { useAccessRequest } from './access';
import {
  SEARCH_LIMIT,
  SEARCH_LIMIT_ALL,
  recordOpened,
  splitHighlight,
  typeLabels,
  useSearch,
  type SearchHit,
  type SearchResponse,
  type SearchType,
} from './search';
import styles from './SearchPalette.module.css';

export interface SearchPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface Option {
  id: string;
  type: SearchType;
  href: string;
  title: string;
  subtitle: string | null;
  /** Recent entries are not highlighted; hits are. */
  highlight: boolean;
}

const DEBOUNCE_MS = 180;
const PLACEHOLDER = 'Kontakt, Deal, Objekt, Einheit, Ticket oder Dokument';

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (value === debounced) return;
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay, debounced]);
  return debounced;
}

function Highlight({ text, query }: { text: string; query: string }) {
  const parts = splitHighlight(text, query);
  if (!parts) return <>{text}</>;
  return (
    <>
      {parts[0]}
      <mark className={styles.mark}>{parts[1]}</mark>
      {parts[2]}
    </>
  );
}

/** Group header: eyebrow label + count, optional right-aligned link. */
function GroupHeader({
  label,
  count,
  action,
}: {
  label: string;
  count?: number | undefined;
  action?: ReactNode;
}) {
  return (
    <div className={styles.groupHead}>
      <Eyebrow as="div">{label}</Eyebrow>
      {count !== undefined ? <span className={styles.groupCount}>{count}</span> : null}
      {action ? <span className={styles.groupAction}>{action}</span> : null}
    </div>
  );
}

/** The ⌘K palette of Shell 03–06 (desktop dialog under the topbar, full-screen on mobile). */
export function SearchPalette({ open, onClose }: SearchPaletteProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mobile = useIsMobile();
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(SEARCH_LIMIT);
  const [scope, setScope] = useState<'all' | 'documents'>('all');
  const [activeIndex, setActiveIndex] = useState(0);
  const debounced = useDebounced(query.trim(), DEBOUNCE_MS);
  const result = useSearch(debounced, limit, open);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const access = useAccessRequest();
  const shortcutMeta = isMacPlatform() ? '⌘' : 'Strg';

  useEscapeKey(onClose, open);
  useFocusTrap(panelRef, open, { initialFocus: inputRef });
  useEffect(() => (open ? lockBodyScroll() : undefined), [open]);

  const resetAccess = access.reset;
  useEffect(() => {
    if (!open) {
      setQuery('');
      setLimit(SEARCH_LIMIT);
      setScope('all');
      setActiveIndex(0);
      resetAccess();
    }
  }, [open, resetAccess]);

  const data: SearchResponse | undefined = result.data;
  const searching = debounced.length > 0;

  const groups = useMemo(() => {
    if (!data || !searching) return [];
    const list =
      scope === 'documents' ? data.groups.filter((group) => group.type === 'document') : data.groups;
    return list.map((group) => ({
      group,
      visible: group.hits.filter((hit) => !hit.locked && hit.href !== null),
      locked: group.hits.filter((hit) => hit.locked),
    }));
  }, [data, searching, scope]);

  const totalHits = groups.reduce((sum, entry) => sum + entry.group.total, 0);

  const options = useMemo<Option[]>(() => {
    if (!data) return [];
    if (!searching) {
      return data.recent.map((item) => ({
        id: `${item.type}:${item.id}`,
        type: item.type,
        href: item.href,
        title: item.title,
        subtitle: typeLabels[item.type],
        highlight: false,
      }));
    }
    const list: Option[] = [];
    for (const entry of groups) {
      for (const hit of entry.visible) {
        list.push({
          id: `${hit.type}:${hit.id}`,
          type: hit.type,
          href: hit.href ?? '',
          title: hit.title,
          subtitle: hit.subtitle,
          highlight: true,
        });
      }
    }
    return list;
  }, [data, groups, searching]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debounced, scope, options.length]);

  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const openOption = useCallback(
    (option: Option, newTab: boolean) => {
      void recordOpened(option.type, option.id.slice(option.type.length + 1)).then((recorded) => {
        // "Zuletzt geöffnet" of the empty palette is served from the cache: refresh it.
        if (recorded) void queryClient.invalidateQueries({ queryKey: ['search', ''] });
      });
      if (newTab) {
        window.open(option.href, '_blank', 'noopener');
        return;
      }
      onClose();
      void navigate(option.href);
    },
    [navigate, onClose, queryClient],
  );

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (options.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + options.length) % options.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(options.length - 1);
    } else if (event.key === 'Enter') {
      const option = options[activeIndex];
      if (!option) return;
      event.preventDefault();
      openOption(option, event.metaKey || event.ctrlKey);
    }
  }

  function goTo(href: string) {
    onClose();
    void navigate(href);
  }

  if (!open) return null;

  const active = options[activeIndex];
  const activeId = active ? `${listboxId}-${active.id}` : undefined;

  function renderOption(option: Option, index: number, trailing?: ReactNode) {
    const selected = index === activeIndex;
    return (
      <div
        key={option.id}
        id={`${listboxId}-${option.id}`}
        role="option"
        aria-selected={selected}
        className={clsx(styles.row, selected && styles.rowActive)}
        onMouseMove={() => {
          if (!selected) setActiveIndex(index);
        }}
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => openOption(option, event.metaKey || event.ctrlKey)}
      >
        <span className={styles.rowText}>
          <span className={styles.rowTitle}>
            {option.highlight ? <Highlight text={option.title} query={debounced} /> : option.title}
          </span>
          {option.subtitle ? <span className={styles.rowSub}>{option.subtitle}</span> : null}
        </span>
        {trailing}
      </div>
    );
  }

  function renderLocked(hit: SearchHit) {
    return (
      <div key={`${hit.type}:${hit.id}`} className={styles.rowLocked} aria-disabled="true">
        <span className={styles.rowText}>
          <span className={styles.rowTitleLocked}>{hit.title}</span>
          <span className={styles.rowSubLocked}>{typeLabels[hit.type]} · Inhalt verborgen</span>
        </span>
        <span className={styles.lockBadge}>
          <Icon icon={Lock} size={16} />
          kein Zugriff
        </span>
      </div>
    );
  }

  function renderGroups() {
    let optionIndex = 0;
    return groups.map(({ group, visible, locked }) => {
      const shownCount = Math.max(visible.length, group.total - locked.length);
      const showVisible = visible.length > 0 || (group.total > 0 && locked.length === 0);
      const canExpand = group.total > group.hits.length && limit < SEARCH_LIMIT_ALL;
      const visibleRows = visible.map(() => {
        const index = optionIndex;
        optionIndex += 1;
        const option = options[index];
        return option ? renderOption(option, index) : null;
      });
      return (
        <div key={group.type}>
          {showVisible ? (
            <div className={styles.group}>
              <GroupHeader
                label={group.label}
                count={shownCount}
                action={
                  canExpand ? (
                    <TextLink as="button" size="small" onClick={() => setLimit(SEARCH_LIMIT_ALL)}>
                      alle {group.total}
                    </TextLink>
                  ) : undefined
                }
              />
              {visible.length > 0 ? visibleRows : <div className={styles.noHits}>keine Treffer</div>}
            </div>
          ) : null}
          {locked.length > 0 ? (
            <div className={styles.group}>
              <GroupHeader label={`${group.label} · außerhalb Ihrer Rechte`} count={locked.length} />
              {locked.map(renderLocked)}
              <div className={styles.lockedNote}>
                Sie sehen nur, dass es diese Einträge gibt.{' '}
                {access.done ? (
                  <span className={styles.lockedDone}>Zugriff beantragt.</span>
                ) : (
                  <TextLink
                    as="button"
                    size="small"
                    disabled={access.pending}
                    onClick={() =>
                      void access.submit({
                        kind: 'records',
                        entity: group.type,
                        ids: locked.map((hit) => hit.id),
                        query: debounced,
                      })
                    }
                  >
                    Zugriff beantragen
                  </TextLink>
                )}
              </div>
              {access.error ? (
                <div className={styles.inlineBanner}>
                  <Banner tone="danger">{access.error}</Banner>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      );
    });
  }

  function renderBody() {
    if (result.isError) {
      return (
        <div className={styles.inlineBanner}>
          <Banner tone="danger">{errorMessage(result.error)}</Banner>
        </div>
      );
    }
    if (!data) {
      return (
        <div className={styles.skeleton} aria-busy="true">
          <Skeleton width="34%" />
          <Skeleton width="58%" />
          <Skeleton width="46%" />
        </div>
      );
    }
    if (!searching) {
      return (
        <>
          {data.recent.length > 0 ? (
            <div className={styles.group}>
              <GroupHeader label="Zuletzt geöffnet" />
              {options.map((option, index) => renderOption(option, index))}
            </div>
          ) : null}
          {data.quickActions.length > 0 ? (
            <div className={styles.group}>
              <GroupHeader label="Schnellzugriff" />
              <div className={styles.quickActions}>
                {data.quickActions.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    className={clsx('pressable', styles.quickAction)}
                    onClick={() => goTo(action.href)}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </>
      );
    }
    if (totalHits === 0) {
      return (
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>Keine Treffer für „{debounced}“</div>
          {data.suggestion ? (
            <div className={styles.emptyHint}>
              Meinten Sie{' '}
              <TextLink
                as="button"
                size="small"
                className={styles.suggestion}
                onClick={() => setQuery(data.suggestion ?? '')}
              >
                {data.suggestion}
              </TextLink>
              ?
            </div>
          ) : null}
          <div className={styles.emptyActions}>
            <Button
              variant="secondary"
              size="small"
              onClick={() => goTo(`/contacts/new?name=${encodeURIComponent(debounced)}`)}
            >
              Neuen Kontakt anlegen
            </Button>
            {scope === 'all' ? (
              <Button variant="secondary" size="small" onClick={() => setScope('documents')}>
                Nur Dokumente durchsuchen
              </Button>
            ) : (
              <Button variant="secondary" size="small" onClick={() => setScope('all')}>
                Alles durchsuchen
              </Button>
            )}
          </div>
        </div>
      );
    }
    return renderGroups();
  }

  const list = (
    <div
      ref={listRef}
      id={listboxId}
      role="listbox"
      aria-label="Suchergebnisse"
      className={clsx(styles.body, result.isFetching && styles.bodyFetching)}
    >
      {scope === 'documents' && totalHits > 0 ? (
        <div className={styles.scopeNote}>
          Nur Dokumente ·{' '}
          <TextLink as="button" size="small" onClick={() => setScope('all')}>
            Alles durchsuchen
          </TextLink>
        </div>
      ) : null}
      {renderBody()}
    </div>
  );

  const input = (
    <input
      ref={inputRef}
      type="text"
      value={query}
      onChange={(event) => setQuery(event.target.value)}
      onKeyDown={onKeyDown}
      placeholder={PLACEHOLDER}
      aria-label="Suchen"
      role="combobox"
      aria-expanded="true"
      aria-controls={listboxId}
      aria-activedescendant={activeId}
      aria-autocomplete="list"
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      enterKeyHint="search"
      className={clsx(styles.input, mobile && styles.inputMobile)}
    />
  );

  if (mobile) {
    return createPortal(
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Suche"
        tabIndex={-1}
        className={styles.mobile}
      >
        <div className={styles.mobileHead}>
          <div className={styles.mobileField}>{input}</div>
          <TextLink as="button" size="small" onClick={onClose}>
            Abbrechen
          </TextLink>
        </div>
        {list}
      </div>,
      document.body,
    );
  }

  return createPortal(
    <Overlay
      top={60}
      style={{ paddingTop: 14, backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
      onScrimClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Suche"
        tabIndex={-1}
        className={styles.dialog}
      >
        <div className={styles.head}>
          {input}
          <KeyboardHint keys="Esc" size="default" />
        </div>
        {list}
        <div className={styles.foot}>
          <span>↑↓ auswählen</span>
          <span>↵ öffnen</span>
          <span>{shortcutMeta}↵ in neuem Tab</span>
          <span className={styles.footEnd}>Sucht nur in Daten, die Sie sehen dürfen</span>
        </div>
      </div>
    </Overlay>,
    document.body,
  );
}
